// Tiny fetch wrapper. All calls go through the Express proxy at /api.

// Status codes are not error messages. Every one of these used to reach the customer as
// the entire text of a failure ("Request failed: 502"), because the proxy returns HTML for
// gateway errors and res.json() threw, leaving nothing else to say.
const STATUS_TEXT = {
  400: "That request wasn't valid.",
  401: "Your session has expired. Please sign in again.",
  403: "You don't have permission to do that.",
  404: "That wasn't found — it may have been deleted.",
  409: "That conflicts with the current state. Reload and try again.",
  423: "That's locked right now.",
  429: "Too many requests — wait a moment and try again.",
  502: "The panel couldn't reach the platform. This is usually brief — try again shortly.",
  503: "That service isn't available right now.",
  504: "The platform took too long to respond. It may still be working on it.",
};

// ── Deploy gates ─────────────────────────────────────────────────────────────
// The server refuses create/deploy/start/restart/rollback with one of these codes until the
// account is set up and paid for. They are announced on a window event so ONE modal in the
// app shell (components/Onboarding.jsx → GateHost) can explain what is missing and link to
// the fix, whichever page or button triggered the refusal. `plan_required` is deliberately
// not global: the fix lives on the service that raised it, so ServiceDetail handles it.
export const GATE_EVENT = "dd:gate";
const GLOBAL_GATE = {
  403: new Set(["onboarding_required"]),
  402: new Set(["insufficient_credit", "billing_setup_required", "account_suspended"]),
};
const MISSING_LABEL = { company: "company name", address: "address", country: "country", email: "contact email", phone: "phone number" };

// Plain-English replacement for whatever the server put in `error`, so a page that renders
// err.message inline can never show a bare "Forbidden" for a gate.
function gateMessage(status, e) {
  if (status === 403 && e.code === "onboarding_required") {
    const todo = (Array.isArray(e.missing) ? e.missing : []).map((m) => MISSING_LABEL[m] || m);
    if (e.emailVerified === false) todo.push("email verification");
    if (e.phoneVerified === false) todo.push("phone verification");
    return todo.length
      ? `Finish setting up your account before deploying. Still needed: ${todo.join(", ")}.`
      : "Finish setting up your account before deploying.";
  }
  if (status === 402 && e.code === "insufficient_credit") {
    const need = Number(e.requiredPence);
    return Number.isFinite(need) && need > 0
      ? `You need at least £${(need / 100).toFixed(2)} of account credit to do that. Top up to continue.`
      : "There isn't enough account credit to do that. Top up to continue.";
  }
  return null;
}

async function req(path, opts = {}) {
  let res;
  try {
    res = await fetch(`/api${path}`, {
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      ...opts,
      body: opts.body ? JSON.stringify(opts.body) : undefined,
    });
  } catch {
    // fetch only rejects on a transport failure, so this is offline / DNS / TLS. The
    // browser's own wording ("Failed to fetch", "NetworkError when attempting…") was
    // being rendered verbatim to customers.
    const err = new Error("Can't reach DebutDeploy. Check your connection and try again.");
    err.offline = true;
    throw err;
  }

  if (!res.ok) {
    const e = await res.json().catch(() => ({})); // HTML error pages land here as {}
    const err = new Error(e.error || STATUS_TEXT[res.status] || `Something went wrong (${res.status}).`);
    err.status = res.status;
    err.detail = e.detail;
    err.code = e.code;          // machine-readable server code (e.g. billing_setup_required)
    err.friendly = e.friendly;  // { code, title, message, fix } when the server recognised it
    err.data = e;               // the whole JSON body: missing[], retryInSec, requiredPence, …
    const method = String(opts.method || "GET").toUpperCase();
    if (method !== "GET" && GLOBAL_GATE[res.status]?.has(e.code)) {
      const friendly = gateMessage(res.status, e);
      if (friendly) err.message = friendly;
      // Callers can skip their own alert() when this is set — the shell is already showing it.
      err.gateHandled = true;
      try { window.dispatchEvent(new CustomEvent(GATE_EVENT, { detail: { ...e, status: res.status, message: err.message } })); } catch { /* non-browser */ }
    }
    // A 401 mid-session means the cookie expired. Every page used to render "Unauthorized"
    // inline and leave the customer stuck on a dead screen with no way back.
    if (res.status === 401 && !/^\/(me|health)$/.test(path)) err.sessionExpired = true;
    throw err;
  }
  return res.json();
}

export const api = {
  totpStatus: () => fetch("/auth/totp/status", { credentials: "same-origin" }).then(async (r) => { const d = await r.json(); if (!r.ok) throw new Error(d.error || "Request failed"); return d; }),
  totpSetup: () => fetch("/auth/totp/setup", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: "{}" }).then(async (r) => { const d = await r.json(); if (!r.ok) throw new Error(d.error || "Request failed"); return d; }),
  totpEnable: (code) => fetch("/auth/totp/enable", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ code }) }).then(async (r) => { const d = await r.json(); if (!r.ok) throw new Error(d.error || "Request failed"); return d; }),
  totpDisable: (code) => fetch("/auth/totp/disable", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ code }) }).then(async (r) => { const d = await r.json(); if (!r.ok) throw new Error(d.error || "Request failed"); return d; }),
  verifyMfa: (code, trustDevice = false) => fetch("/auth/mfa/verify", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ code, trustDevice }) }).then(async (r) => { const d = await r.json(); if (!r.ok) throw new Error(d.error || "Request failed"); return d; }),
  passkeys: () => fetch("/auth/passkeys/status", { credentials: "same-origin" }).then(async (r) => { const d = await r.json(); if (!r.ok) throw new Error(d.error || "Request failed"); return d; }),
  passkeyAuthenticationOptions: (returnTo) => fetch("/auth/passkeys/authenticate/options", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ returnTo }) }).then(async (r) => { const d = await r.json(); if (!r.ok) throw new Error(d.error || "Request failed"); return d; }),
  verifyPasskeyAuthentication: (challenge, response) => fetch("/auth/passkeys/authenticate/verify", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ challenge, response }) }).then(async (r) => { const d = await r.json(); if (!r.ok) throw new Error(d.error || "Request failed"); return d; }),
  passkeyRegistrationOptions: () => fetch("/auth/passkeys/register/options", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: "{}" }).then(async (r) => { const d = await r.json(); if (!r.ok) throw new Error(d.error || "Request failed"); return d; }),
  verifyPasskeyRegistration: (challenge, response) => fetch("/auth/passkeys/register/verify", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ challenge, response }) }).then(async (r) => { const d = await r.json(); if (!r.ok) throw new Error(d.error || "Request failed"); return d; }),
  deletePasskey: (id) => fetch(`/auth/passkeys/${encodeURIComponent(id)}`, { method: "DELETE", credentials: "same-origin" }).then(async (r) => { const d = await r.json(); if (!r.ok) throw new Error(d.error || "Request failed"); return d; }),
  health: () => req("/health"),
  me: () => req("/me"),
  // Auth routes live under /auth (not /api) — req() would hit /api/logout → 404.
  logout: () =>
    fetch("/auth/logout", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
    }).then((res) => {
      if (!res.ok) throw new Error("Couldn't sign you out. Try again, or close the browser.");
      return res.json();
    }),
  services: () => req("/services"),
  service: (id) => req(`/services/${id}`),
  renameService: (id, name) => req(`/services/${id}/rename`, { method: "PATCH", body: { name } }),
  // projects & environments (panel-native)
  projects:        () => req("/projects"),
  project:         (id) => req(`/projects/${id}`),
  adminProject:    (id) => req(`/admin/projects/${id}`),
  createProject:   (name) => req("/projects", { method: "POST", body: { name } }),
  renameProject:   (id, name) => req(`/projects/${id}`, { method: "PATCH", body: { name } }),
  deleteProject:   (id) => req(`/projects/${id}`, { method: "DELETE" }),
  createEnvironment: (projectId, name) => req(`/projects/${projectId}/environments`, { method: "POST", body: { name } }),
  renameEnvironment: (id, name) => req(`/environments/${id}`, { method: "PATCH", body: { name } }),
  deleteEnvironment: (id) => req(`/environments/${id}`, { method: "DELETE" }),
  placeResource:   (type, id, environmentId) => req(`/resources/${type}/${id}/placement`, { method: "PATCH", body: { environmentId } }),
  transferProject: (id, email) => req(`/admin/projects/${id}/transfer`, { method: "POST", body: { email } }), // master-admin only
  updateResources: (id, body) => req(`/services/${id}/resources`, { method: "PATCH", body }), // { cpus?, memory? } — low-level; no pricing, no redeploy
  // Instance-size change as one priced, confirmed operation. previewPlanChange returns the
  // spec/price/proration/cycle the confirmation dialog shows; applyPlanChange then sets the
  // limits, moves the billing, and redeploys so the new CPU/RAM actually takes effect.
  previewPlanChange: (id, body) => req(`/services/${id}/plan-change/preview`, { method: "POST", body }), // { planId|null, cpus?, memory? }
  applyPlanChange:   (id, body) => req(`/services/${id}/plan-change`, { method: "POST", body }),
  // custom domains (Render-style manager)
  listDomains:  (id) => req(`/services/${id}/domains`),
  // → { ok, pending, addedDomains, deploy?, dns? }. pending = DNS isn't pointing here yet, so
  // the domain is saved and bound automatically later; force binds it right now regardless.
  addDomain:    (id, fqdn, { force } = {}) => req(`/services/${id}/domain`, { method: "POST", body: force ? { fqdn, force: true } : { fqdn } }),
  removeDomain: (id, fqdn) => req(`/services/${id}/domains`, { method: "DELETE", body: { fqdn } }),
  // Redeploys so the proxy re-routes the domain and re-requests its certificate.
  retryDomain:  (id, fqdn) => req(`/services/${id}/domains/retry`, { method: "POST", body: { fqdn } }),
  // The ONLY source for "which DNS records to create" → { serverIp, records: [{type,name,value,note}] }
  domainRecords: (id, fqdn) => req(`/services/${id}/domain/records?fqdn=${encodeURIComponent(fqdn)}`),
  verifyDomain: (id, fqdn) => req(`/services/${id}/domain/verify?fqdn=${encodeURIComponent(fqdn)}`), // { pointsToServer, resolvedIps, expectedIp }
  deploy: (id, opts) => req(`/services/${id}/deploy`, { method: "POST", body: opts }), // opts?: { clearCache: true }
  rollback: (id, commit) => req(`/services/${id}/rollback`, { method: "POST", body: { commit } }),
  control: (id, action) => req(`/services/${id}/${action}`, { method: "POST" }),
  deployments: (id) => req(`/services/${id}/deployments`),
  activeDeployments: () => req(`/deployments/active`), // fleet build queue (active + queued)
  deploymentLog: () => req(`/deployments`), // fleet-wide recent deployment history
  cancelDeployment: (uuid) => req(`/deployments/${uuid}/cancel`, { method: "POST" }),
  concurrentBuilds: (serverUuid) => req(`/servers/${serverUuid}/concurrent-builds`), // admin: current lane count
  setConcurrentBuilds: (serverUuid, n) => req(`/servers/${serverUuid}/concurrent-builds`, { method: "PATCH", body: { concurrentBuilds: n } }),
  // Returns the array of {time, level, message} lines; falls back if the shape is older (raw array/string).
  logs: (id) => req(`/services/${id}/logs`).then((d) => (Array.isArray(d?.lines) ? d.lines : Array.isArray(d) ? d : [])),
  metrics: (id) => req(`/services/${id}/metrics`),
  metricsHistory: (id, window = "1h") => req(`/services/${id}/metrics/history?window=${window}`), // { window, series:{cpu,mem,net,throughput,diskio,pids}, stats }
  metricsHost: (window = "1h") => req(`/metrics/host?window=${window}`), // host CPU/RAM/disk % (admin)
  buildLogs: (id) => req(`/services/${id}/build-logs`), // { lines: [{time,type,message}], error? }
  envs: (id) => req(`/services/${id}/envs`),
  envScan: (id) => req(`/services/${id}/env-scan`), // { warnings: [...], scannable }
  revealEnv: (id, key) => req(`/services/${id}/envs/reveal?key=${encodeURIComponent(key)}`),
  saveEnv: (id, body) => req(`/services/${id}/envs`, { method: "POST", body }),
  deleteEnv: (id, envId) => req(`/services/${id}/envs/${envId}`, { method: "DELETE" }),
  // Persistent disks — billed per GB and redeploys the service, so size and cost are
  // previewed and confirmed before anything is created.
  serviceVolumes: (id) => req(`/services/${id}/volumes`),
  previewServiceVolume: (id, body) => req(`/services/${id}/volumes/preview`, { method: "POST", body }), // { sizeGb, action? }
  addServiceVolume: (id, mountPath, sizeGb) => req(`/services/${id}/volumes`, { method: "POST", body: { mountPath, sizeGb } }),
  deleteServiceVolume: (id, vid) => req(`/services/${id}/volumes/${vid}`, { method: "DELETE" }),
  databases: () => req("/databases"),
  createDatabase: (body) => req("/databases", { method: "POST", body }),
  databaseAction: (uuid, action) => req(`/databases/${uuid}/${action}`, { method: "POST" }), // start | stop | restart
  database: (uuid) => req(`/databases/${uuid}`),
  renameDatabase: (uuid, name) => req(`/databases/${uuid}/rename`, { method: "PATCH", body: { name } }),
  deleteDatabase: (uuid) => req(`/databases/${uuid}`, { method: "DELETE" }),
  dbCredentials: (id) => req(`/databases/${encodeURIComponent(id)}/credentials`),
  dbPublicAccess: (id) => req(`/databases/${encodeURIComponent(id)}/public`),
  enableDbPublicAccess: (id, allowlist) => req(`/databases/${encodeURIComponent(id)}/public`, { method: "PUT", body: { allowlist } }),
  disableDbPublicAccess: (id) => req(`/databases/${encodeURIComponent(id)}/public`, { method: "DELETE" }),
  servers: () => req("/servers"),
  getRepos: () =>
    fetch("/api/github/repos", { credentials: "same-origin" }).then((res) => {
      if (res.status === 409) return { needsConnect: true };
      if (!res.ok) throw new Error(STATUS_TEXT[res.status] || "Couldn't load your repositories.");
      return res.json();
    }),
  getBranches: (owner, repo) => req(`/github/repos/${owner}/${repo}/branches`),
  createApp: (body) => req("/apps", { method: "POST", body }),
  // Backups
  getBackupConfig: (id) => req(`/databases/${id}/backups`),
  setBackupSchedule: (id, body) => req(`/databases/${id}/backups`, { method: "POST", body }),
  triggerBackup: (id) => req(`/databases/${id}/backups/run`, { method: "POST" }),
  // Shared vars (admin)
  sharedVars: () => req("/shared-vars"),
  createSharedVar: (body) => req("/shared-vars", { method: "POST", body }),
  deleteSharedVar: (id) => req(`/shared-vars/${id}`, { method: "DELETE" }),
  // Variable groups (org-scoped, attachable to services)
  varGroups:          (reveal) => req(`/var-groups${reveal ? "?reveal=1" : ""}`),
  createVarGroup:     (body) => req("/var-groups", { method: "POST", body }),
  updateVarGroup:     (id, body) => req(`/var-groups/${id}`, { method: "PATCH", body }),
  deleteVarGroup:     (id) => req(`/var-groups/${id}`, { method: "DELETE" }),
  setVarGroupVars:    (id, vars) => req(`/var-groups/${id}/vars`, { method: "POST", body: { vars } }),
  renameVarGroupVar:  (id, key, next) => req(`/var-groups/${id}/vars/${encodeURIComponent(key)}`, { method: "PATCH", body: { key: next } }),
  deleteVarGroupVar:  (id, key) => req(`/var-groups/${id}/vars/${encodeURIComponent(key)}`, { method: "DELETE" }),
  attachVarGroup:     (id, uuid) => req(`/var-groups/${id}/services`, { method: "POST", body: { uuid } }),
  detachVarGroup:     (id, uuid) => req(`/var-groups/${id}/services/${uuid}`, { method: "DELETE" }),
  // Hetzner provisioning (admin)
  hetznerServerTypes: () => req("/hetzner/server-types"),
  hetznerLocations:   () => req("/hetzner/locations"),
  provisionServer:    (body) => req("/servers/provision", { method: "POST", body }),
  customerWorker:     () => req("/admin/customer-worker"),
  provisionCustomerWorker: () => req("/admin/customer-worker/provision", { method: "POST", body: {} }),
  assignCustomerWorker: (serverUuid) => req("/admin/customer-worker", { method: "PUT", body: { serverUuid } }),
  // Destroying a box is deliberately as reachable as creating one — a box you cannot
  // delete from here is a box that runs for eight weeks unused.
  snapshotHetznerServer: (id, description) => req(`/admin/servers/${id}/snapshot`, { method: "POST", body: { description } }),
  deleteHetznerServer:   (id, body) => req(`/admin/servers/${id}`, { method: "DELETE", body }), // { confirm_name, reason?, skip_snapshot? }
  // Hetzner Object Storage admin tools. S3 secrets never return from the API.
  objectStorageStatus:   () => req("/admin/object-storage/status"),
  saveObjectStorageConnection: (body) => req("/admin/object-storage/connection", { method: "PUT", body }),
  removeObjectStorageConnection: () => req("/admin/object-storage/connection", { method: "DELETE", body: {} }),
  objectStorageBuckets: (region) => req(`/admin/object-storage/buckets?region=${encodeURIComponent(region)}`),
  createObjectStorageBucket: (body) => req("/admin/object-storage/buckets", { method: "POST", body }),
  restrictObjectStorageBucket: (bucket, body) => req(`/admin/object-storage/buckets/${encodeURIComponent(bucket)}/restrict`, { method: "POST", body }),
  objectStorageAccessKeys: () => req("/admin/object-storage/access-keys"),
  addObjectStorageAccessKey: (body) => req("/admin/object-storage/access-keys", { method: "POST", body }),
  setObjectStorageLifecycle: (bucket, body) => req(`/admin/object-storage/buckets/${encodeURIComponent(bucket)}/lifecycle`, { method: "POST", body }),
  deleteObjectStorageBucket: (name, region) => req(`/admin/object-storage/buckets/${encodeURIComponent(name)}?region=${encodeURIComponent(region)}`, { method: "DELETE", body: {} }),
  objectStorageObjects: (bucket, region, cursor) => req(`/admin/object-storage/buckets/${encodeURIComponent(bucket)}/objects?region=${encodeURIComponent(region)}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`),
  deleteObjectStorageObject: (bucket, region, key) => req(`/admin/object-storage/buckets/${encodeURIComponent(bucket)}/objects?region=${encodeURIComponent(region)}&key=${encodeURIComponent(key)}`, { method: "DELETE", body: {} }),
  configureObjectStorageCors: (bucket, body) => req(`/admin/object-storage/buckets/${encodeURIComponent(bucket)}/cors`, { method: "POST", body }),
  objectStorageServices: () => req("/admin/object-storage/services"),
  attachObjectStorageBucket: (bucket, body) => req(`/admin/object-storage/buckets/${encodeURIComponent(bucket)}/attach`, { method: "POST", body }),
  composeServices: () => req("/admin/coolify/compose-services"),
  composeService: (uuid) => req(`/admin/coolify/compose-services/${encodeURIComponent(uuid)}`),
  saveComposeService: (uuid, composeYaml) => req(`/admin/coolify/compose-services/${encodeURIComponent(uuid)}`, { method: "PATCH", body: { composeYaml } }),
  deployComposeService: (uuid) => req(`/admin/coolify/compose-services/${encodeURIComponent(uuid)}/deploy`, { method: "POST", body: {} }),
  uploadObjectStorageObject: async (bucket, region, key, file) => {
    const query = new URLSearchParams({ region, key });
    const response = await fetch(`/api/admin/object-storage/buckets/${encodeURIComponent(bucket)}/objects?${query}`, {
      method: "PUT", credentials: "same-origin", headers: { "Content-Type": "application/octet-stream", "x-object-content-type": file.type || "application/octet-stream" }, body: file,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw Object.assign(new Error(data.error || STATUS_TEXT[response.status] || "Upload failed."), { status: response.status });
    return data;
  },
  provisionStatus:    (id) => req(`/servers/${id}/provision-status`),
  // Business email hosting (admin)
  mailStatus:      () => req("/mail/status"),
  mailDomains:     () => req("/mail/domains"),
  createMailDomain:(domain, orgId) => req("/mail/domains", { method: "POST", body: { domain, orgId } }),
  deleteMailDomain:(domain) => req(`/mail/domains/${encodeURIComponent(domain)}`, { method: "DELETE" }),
  // Who pays for this domain's mailboxes. Cascades to the mailbox rows billing counts.
  assignMailDomain:(domain, orgId) => req(`/mail/domains/${encodeURIComponent(domain)}`, { method: "PATCH", body: { orgId } }),
  // Import what the mail server actually has into the panel's billing tables. Safe to re-run.
  reconcileMail:  () => req("/mail/reconcile", { method: "POST" }),
  createMailbox:   (body) => req("/mail/mailboxes", { method: "POST", body }), // { address, password, quotaMb }
  deleteMailbox:   (address) => req(`/mail/mailboxes/${encodeURIComponent(address)}`, { method: "DELETE" }),
  // Omit `password` for a generated temp one. The plaintext comes back ONCE — mailcow
  // stores only a hash, so it can never be read again. Show it, don't persist it.
  // Recovery address = the user's OTHER email; without one they cannot self-serve a reset.
  setMailboxRecovery: (address, recoveryEmail) =>
    req(`/mail/mailboxes/${encodeURIComponent(address)}/recovery`, { method: "POST", body: { recoveryEmail } }),
  clearMailboxRecovery: (address) =>
    req(`/mail/mailboxes/${encodeURIComponent(address)}/recovery`, { method: "DELETE" }),
  resetMailboxPassword: (address, password) =>
    req(`/mail/mailboxes/${encodeURIComponent(address)}/password`, { method: "POST", body: password ? { password } : {} }),
  verifyMailDns:   (domain) => req(`/mail/domains/${encodeURIComponent(domain)}/verify`), // { checks:[{key,label,ok,detail}] }
  // One-click DNS (Domain Connect)
  dnsDiscover: (domain, kind, serviceId) => req(`/dns/discover?domain=${encodeURIComponent(domain)}&kind=${kind}${serviceId ? `&serviceId=${encodeURIComponent(serviceId)}` : ""}`),
  dnsStatus:   (domain, kind) => req(`/dns/status?domain=${encodeURIComponent(domain)}&kind=${kind}`),
  // Customers + billing (admin)
  customers: () => req("/customers"),
  billing: () => req("/billing"),
  plans: () => req("/plans"), // customer-facing priced presets { compute:[], db:[] }
  // Deploy-key service creation (admin)
  prepareDeployKey: () => req("/git/prepare-key", { method: "POST" }),
  createGitService: (body) => req("/git/create-service", { method: "POST", body }),
  // GitHub
  githubInstallations: () => req("/github/installations"),
  // Render importer (admin)
  renderKeys:       () => req("/render/keys"),
  saveRenderKey:    (body) => req("/render/keys", { method: "POST", body }),
  deleteRenderKey:  (id) => req(`/render/keys/${id}`, { method: "DELETE" }),
  renderServices:  (creds) => req("/import/render/services", { method: "POST", body: creds }),
  renderDatabases: (creds) => req("/import/render/databases", { method: "POST", body: creds }),
  importRender:   (body) => req("/import/render", { method: "POST", body }),
  importRenderProject: (body) => req("/import/render/project", { method: "POST", body }),
  // Provision one dedicated box + import a group of services onto it (admin).
  importRenderGroup: (body) => req("/import/render/dedicated-group", { method: "POST", body }),
  // Activity & notifications
  events:           (limit) => req(`/events${limit ? `?limit=${limit}` : ""}`),
  serviceEvents:    (id) => req(`/services/${id}/events`),
  getNotifications: () => req("/notifications"),
  saveNotifications:(body) => req("/notifications", { method: "PUT", body }),
  // API keys (programmatic access — also used as the MCP DEBUTDEPLOY_TOKEN)
  tokens: () => req("/tokens"),
  createToken: (body) => req("/tokens", { method: "POST", body }), // { name, scope: 'full'|'read' } → { token } shown once
  deleteToken: (id) => req(`/tokens/${id}`, { method: "DELETE" }),
  // Org + team
  org: () => req("/org"),
  orgMembers: () => req("/org/members"),
  createInvite: (body) => req("/org/invites", { method: "POST", body }),
  orgInvites: () => req("/org/invites"),
  revokeInvite: (id) => req(`/org/invites/${id}`, { method: "DELETE" }),
  acceptInvite: (token) => req("/org/invites/accept", { method: "POST", body: { token } }),
  setMemberRole: (userId, role) => req(`/org/members/${userId}`, { method: "PATCH", body: { role } }),
  removeMember: (userId) => req(`/org/members/${userId}`, { method: "DELETE" }),
  // Master Admin orgs
  adminOrgs: () => req("/admin/orgs"),
  adminOrg: (id) => req(`/admin/orgs/${id}`),
  // Billing (prepaid wallet)
  wallet: () => req("/billing/wallet"),
  topup: (amount_pence) => req("/billing/topup", { method: "POST", body: { amount_pence } }),
  reconcileTopups: () => req("/billing/topup/reconcile", { method: "POST" }),
  adminReconcileTopups: (id) => req(`/admin/orgs/${id}/topups/reconcile`, { method: "POST" }),
  billingPortal: () => req("/billing/portal", { method: "POST" }),
  startMySubscription: () => req("/billing/subscribe", { method: "POST" }),
  autoRecharge: () => req("/billing/autorecharge"),
  setAutoRecharge: (body) => req("/billing/autorecharge", { method: "PATCH", body }),
  setServicePlan: (id, planId) => req(`/services/${id}/plan`, { method: "PATCH", body: { planId } }),
  setDatabasePlan: (id, planId) => req(`/databases/${id}/plan`, { method: "PATCH", body: { planId } }),
  updateDatabaseResources: (id, body) => req(`/databases/${id}/resources`, { method: "PATCH", body }), // { memory }
  // Support sessions: scope the operator's view to one customer, time-boxed and audited.
  supportSession: () => req("/admin/support"),
  openSupport: (orgId, reason) => req("/admin/support", { method: "POST", body: { org_id: orgId, reason } }),
  setSupportWrite: (write) => req("/admin/support", { method: "PATCH", body: { write } }),
  closeSupport: () => req("/admin/support", { method: "DELETE" }),
  invoices: () => req("/billing/invoices"),
  adminInvoices: (id) => req(`/admin/orgs/${id}/invoices`),
  // Client self-service billing (org owner)
  orgBillingInfo: () => req("/org/billing-info"),
  saveOrgBillingInfo: (body) => req("/org/billing-info", { method: "PATCH", body }),
  // Onboarding: company details + verified email & phone, required before anything deploys.
  orgOnboarding: () => req("/org/onboarding"),
  sendEmailCode: () => req("/org/verify/email/send", { method: "POST" }),
  confirmEmailCode: (code) => req("/org/verify/email/confirm", { method: "POST", body: { code } }),
  sendPhoneCode: () => req("/org/verify/phone/send", { method: "POST" }),
  confirmPhoneCode: (code) => req("/org/verify/phone/confirm", { method: "POST", body: { code } }),
  adminOrgOnboarding: (id) => req(`/admin/orgs/${id}/onboarding`),
  adminSetVerification: (id, channel, verified) => req(`/admin/orgs/${id}/verification`, { method: "POST", body: { channel, verified } }),
  // Every service and database owned by any client, in one list (operator view).
  adminClientServices: () => req("/admin/client-services"),
  orgInvoiceUrl: (period, download) => `/api/org/invoice${period ? `?period=${period}` : ""}${download ? `${period ? "&" : "?"}download=1` : ""}`,
  adminInvoiceUrl: (id, period, download) => `/api/admin/orgs/${id}/invoice${period ? `?period=${period}` : ""}${download ? `${period ? "&" : "?"}download=1` : ""}`,
  // Usage metering
  usage: (period) => req(`/org/usage${period ? `?period=${period}` : ""}`),
  usageCurrent: () => req("/org/usage/current"),
  adminOrgUsage: (id, period) => req(`/admin/orgs/${id}/usage${period ? `?period=${period}` : ""}`),
  adminOrgWallet: (id) => req(`/admin/orgs/${id}/wallet`),
  adminOrgPayments: (id) => req(`/admin/orgs/${id}/payments`),
  adminOrgResources: (id) => req(`/admin/orgs/${id}/resources`),
  adminOrgProjects: (id) => req(`/admin/orgs/${id}/projects`), // client's folders — admins drill in from Clients
  adminTransferTargets: () => req("/admin/transfer-targets"),
  adminTransferCandidates: (id) => req(`/admin/orgs/${id}/transfer-candidates`),
  adminTransferService: (id, body) => req(`/admin/orgs/${id}/resources/transfer-service`, { method: "POST", body }),
  adminOrgBillingInfo: (id) => req(`/admin/orgs/${id}/billing-info`),
  adminSaveBillingInfo: (id, body) => req(`/admin/orgs/${id}/billing-info`, { method: "PATCH", body }),
  adminAdjustCredit: (id, body) => req(`/admin/orgs/${id}/credit`, { method: "POST", body }),
  orgBilling: (id) => req(`/admin/orgs/${id}/billing`),
  orgServicePlans: (id) => req(`/admin/orgs/${id}/service-plans`),
  setOrgCurrency: (id, currency) => req(`/admin/orgs/${id}/currency`, { method: "PUT", body: { currency } }),
  subscribeOrg: (id) => req(`/admin/orgs/${id}/subscribe`, { method: "POST" }),
  setOrgComp: (id, body) => req(`/admin/orgs/${id}/comp`, { method: "PATCH", body }),
  adminEmailTemplates: () => req("/admin/email-templates"),
  adminEmailTemplate: (key) => req(`/admin/email-templates/${encodeURIComponent(key)}`),
  saveAdminEmailTemplate: (key, body) => req(`/admin/email-templates/${encodeURIComponent(key)}`, { method: "PATCH", body }),
  previewAdminEmailTemplate: (key, body) => req(`/admin/email-templates/${encodeURIComponent(key)}/preview`, { method: "POST", body }),
  sendAdminEmailTemplateTest: (key, to) => req(`/admin/email-templates/${encodeURIComponent(key)}/test-send`, { method: "POST", body: { to } }),
  // Stripe admin dashboard (operator) — see data + flip test/live without a Stripe login
  stripeConfig: () => req("/admin/stripe/config"),
  stripeOverview: () => req("/admin/stripe/overview"),
  setStripeMode: (mode) => req("/admin/stripe/mode", { method: "PUT", body: { mode } }),
  syncStripeCatalog: () => req("/admin/stripe/catalog", { method: "POST" }),
  // Fleet monitoring
  fleetOverview: () => req("/fleet/overview"),
  fleetHygiene:  () => req("/fleet/hygiene"),
  restartService: (id) => req(`/services/${id}/restart`, { method: "POST" }),
  // Unwedge one service's stuck deploys: fails its in_progress/queued rows, drops the
  // hung build helpers, nudges the worker. Scoped to that service only.
  clearDeployQueue: (id) => req(`/services/${id}/clear-queue`, { method: "POST" }),
  // Master-admin: consoles behind the platform, and host power. rebootHost echoes the
  // IP back as `confirm` — the server rejects the call without it.
  adminPortals: () => req("/admin/portals"),
  rebootHost: (ip) => req("/admin/hosts/reboot", { method: "POST", body: { ip, confirm: ip } }),
  situations: () => req("/situations"),
  remediateSituation: (id) => req(`/situations/${id}/remediate`, { method: "POST" }),
};
